import React from "react";
import { TabsProps as MuiTabsProps } from "@mui/material/Tabs";
import StyledTabs from "./Tabs.styled";
import StyledTab, {
  StyledIndication,
  StyledIndicationLabel,
} from "./Tab.styled";
import { Icon } from "../Icon";

export interface TabItem {
  iconName?: string;
  label: string;
  disabled?: boolean;
  indicationNumber?: number;
  indicationType?: "warning" | "success";
}

/**
 * Enforce the tabs to have either all with "iconName" or all without "iconName"
 * Will throw an error if have mixed variants
 */
type With<T, K extends keyof T> = T & Required<Pick<T, K>>;
type Without<T, K extends keyof T> = T & Partial<Record<K, never>>;
type ArrayAllWithOrAllWithout<T, K extends keyof T> =
  | With<T, K>[]
  | Without<T, K>[];

/**
 * Either pass all items with "iconName" or leave it out
 * // This will throw error on second object because of missing iconName
 * const arrayOfObjects: ObjectsAllWithOrAllWithoutIcon = [
 *   { iconName: "box", label: "storage" },
 *   { label: "memory" }, // Error: Property 'iconName' is missing
 * ];
 *
 * // This will not give any error because both has iconName
 * const arrayOfObjects2: ObjectsAllWithOrAllWithoutIcon = [
 *   { iconName: "box", label: "storage" },
 *   { iconName: "circle", label: "memory" },
 * ];
 *
 * // This will not give any error because no object using iconName
 * const arrayOfObjects3: ObjectsAllWithOrAllWithoutIcon = [
 *   { label: "storage" },
 *   { label: "memory" },
 * ];
 */
export type TabsAllWithOrAllWithoutIcon = ArrayAllWithOrAllWithout<
  TabItem,
  "iconName"
>;

export interface TabsProps extends MuiTabsProps {
  /**
   * An array of tab items to be displayed within the Tabs component.
   * Each item should have a 'label' property that defines the tab's label text.
   * Each item may have 'iconName' property, but you either pass all tabs an icon, or without icons at all.
   * Optionally, you can set the 'disabled' property to true to disable a specific tab.
   *
   * Example:
   * ```
   * const tabs = [
   *   { label: 'Tab 1' },
   *   { label: 'Tab 2', disabled: true },
   *   { label: 'Tab 3' },
   * ];
   * ```
   */
  tabs: TabsAllWithOrAllWithoutIcon;
  /**
   * The type of tabs to render, which determines their appearance.
   * - 'primary': Renders primary style tabs.
   * - 'secondary': Renders secondary style tabs.
   */
  tabsType?: "primary" | "secondary";
  /**
   * Whether to disable the hover underline effect on tabs.
   */
  disableHoverUnderline?: boolean;
}

export default function Tabs({
  tabs,
  tabsType = "primary",
  disableHoverUnderline,
  ...props
}: TabsProps) {
  return (
    <StyledTabs tabsType={tabsType} {...props}>
      {tabs.map((tab, index) => (
        <StyledTab
          data-testid={`${tab.label}-tab`}
          key={index}
          label={
            tab.indicationNumber ? (
              <StyledIndicationLabel>
                {tab.label}
                <StyledIndication
                  indicationType={tab.indicationType || "warning"}
                >
                  {tab.indicationNumber}
                </StyledIndication>
              </StyledIndicationLabel>
            ) : (
              tab.label
            )
          }
          icon={tab.iconName && <Icon name={tab.iconName} size="big" />}
          tabsType={tabsType}
          disabled={tab.disabled}
          disableHoverUnderline={disableHoverUnderline}
        />
      ))}
    </StyledTabs>
  );
}
